import React from 'react';
import classnames from 'classnames';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import { sameArrays } from './FormEditor';
import EnumEditor from './EnumFormEditor';
import TextFieldFormEditor from './TextFieldFormEditor';
import BooleanFormEditor from './BooleanFormEditor';
import { parseValidator } from './validators';
import withHelp from '../withHelp';

const isComparable = v => ((v !== undefined) && (v !== null) && !Number.isNaN(v));

const sameData = (obj1, obj2, deep) => {
  if (obj1 === obj2) return true;
  // For the scope of comparing form data, NULL and undefined are considered equal
  const keys1 = Object.keys(obj1).filter(k => isComparable(obj1[k]));
  const keys2 = Object.keys(obj2).filter(k => isComparable(obj2[k]));
  let count2 = keys2.length;
  const diff = keys1.find((k) => {
    const v1 = obj1[k];
    const v2 = obj2[k];
    count2 -= 1;
    if (v1 === v2) {
      return false;
    }
    if (v2 === undefined) { // obj2 has been stripped from undefined props so this means props was not there
      return true;
    }
    return deep && (typeof v2 === 'object') ? !sameData(v1, v2, true) : true;
  });
  return !diff && !count2;
};

const EditorFactories = {
  enum: EnumEditor,
  string: TextFieldFormEditor,
  number: TextFieldFormEditor,
  integer: TextFieldFormEditor,
  password: TextFieldFormEditor,
  bool: BooleanFormEditor,
};

const makeKey = i => i.toString();

const renderLayoutComponents = (lcomps, props, forceAll) => {
  return (forceAll ? lcomps : lcomps.filter(lc => (!lc.canDisplay || lc.canDisplay(props))))
  .map((lc, i) => {
    return lc.Component
      ? <lc.Component {...props} key={makeKey(i)} />
      : lc.render(props);
  });
};

const LayoutTypes = {
  section: ({ config, children, key, viewService }) => {
    const { title, icon, message, className, help } = config;
    const blanckIcon = icon === 'blanck';
    const Icon = !blanckIcon && icon ? viewService.getIcon(icon) : undefined;
    return {
      Component: withHelp(({ helpNode, ...props }) => (
        <section key={key} className={classnames('grb-form-section', className)}>
          <header>
            {(!!Icon || blanckIcon) && (
              <div className="grb-form-row-icon">
                {!!Icon && <Icon />}
              </div>
            )}
            <Typography variant="h6">{title}</Typography>
            {!!help && (help.placement !== 'title') && (
              <div className="grb-flex" />
            )}
            {!!help && (
              <div className="grb-form-row-icon grb-left-gutter">
                {helpNode}
              </div>
            )}
          </header>
          <Paper className="grb-form-section-body" elevation={4}>
            {!!message && (
              <Typography className="grb-form-info" key={`${key}-info`}>{message}</Typography>
            )}
            {renderLayoutComponents(children, props)}
          </Paper>
        </section>
      ), help),
    };
  },
  subsection: ({ config, children, key, viewService, parseLayoutItem }) => {
    const { title, icon, message, className, help } = config;
    const blanckIcon = icon === 'blanck';
    const Icon = !blanckIcon && icon ? viewService.getIcon(icon) : undefined;
    const footer = config.footer ? config.footer.map(parseLayoutItem) : null;
    return {
      Component: withHelp(({ helpNode, ...props }) => {
        return (
          <section key={key} className={classnames('grb-form-subsection', className)}>
            <header>
              {(!!Icon || blanckIcon) && (
                <div className="grb-form-row-icon">
                  {!!Icon && <Icon />}
                </div>
              )}
              <Typography variant="body1">{title}</Typography>
              {!!help && (help.placement !== 'title') && (
                <div className="grb-flex" />
              )}
              {!!help && (
                <div className="grb-form-row-icon grb-left-gutter">
                  {helpNode}
                </div>
              )}
            </header>
            <div className="grb-form-subsection-body">
              {!!message && (
                <Typography className="grb-form-info" key={`${key}-info`}>{message}</Typography>
              )}
              {renderLayoutComponents(children, props)}
            </div>
            {footer && (
              <footer className="grb-form-block-footer">
                {renderLayoutComponents(footer, props, true)}
              </footer>
            )}
          </section>
        );
      }, help),
    };
  },
  'icon-row': ({ config, children, key, viewService }) => {
    const { icon, className, description, caption, indent } = config;
    const Icon = icon ? viewService.getIcon(icon) : undefined;
    const checkboxRow = children.length && !children.find(c => !c.checkboxRow);
    return {
      checkboxRow,
      iconRow: true,
      Component: (props) => (
        <div
          key={key}
          className={classnames(
            'grb-form-row',
            'grb-form-row-with-icon',
            checkboxRow && 'grb-form-row-checkbox',
            className,
          )}
        >
          <div className="grb-form-row-icon">
            {Icon && <Icon />}
          </div>
          {(description || caption) ? (
            <div className="grb-form-row-content">
              {!!description && (
                <div className="grb-form-row-description">
                  {description.split('\n').map(d => (
                    <Typography>{d}</Typography>
                  ))}
                </div>
              )}
              <div className={classnames('grb-form-row', indent && `grb-form-row-indent_${indent}`)}>
                {renderLayoutComponents(children, props)}
              </div>
              {!!caption && (
                <div className="grb-form-row-caption">
                  {caption.split('\n').map(d => (
                    <Typography variant="caption">{d}</Typography>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className={classnames('grb-form-row', indent && `grb-form-row-indent_${indent}`)}>
              {renderLayoutComponents(children, props)}
            </div>
          )}
        </div>
      ),
    };
  },
  row: ({ config, children, key }) => {
    const { className } = config;
    const checkboxRow = !children.find(c => c.checkboxRow);
    return {
      checkboxRow,
      Component: (props) => (
        <div key={key} className={classnames('grb-form-row', checkboxRow && 'grb-form-row-checkbox', className)}>
          {renderLayoutComponents(children, props)}
        </div>
      ),
    };
  },
  column: ({ config, children, key }) => {
    const { className } = config;
    return {
      render: (props) => {
        return (
          <div key={key} className={classnames('grb-form-column', className)}>
            {renderLayoutComponents(children, props)}
          </div>
        );
      },
    };
  },
  label: ({ config, children, key, viewService }) => {
    const { className, label, icon, help } = config;
    const Icon = icon ? viewService.getIcon(icon) : undefined;
    return {
      Component: withHelp(({ helpNode, ...props }) => (
        <div key={key} className={classnames('grb-form-label', className)}>
          {Icon && <Icon />}
          <Typography className="grb-form-label-text">{label}</Typography>
          {renderLayoutComponents(children, props)}
          <div className="grb-flex" />
          {helpNode}
        </div>
      ), help),
    };
  },
  block: ({ config, key, children, viewService, parseLayoutItem }) => {
    const { className, title, icon, variant = 'h6', topGutter, bottomGutter, help } = config;
    const Icon = icon ? viewService.getIcon(icon) : undefined;
    const footer = config.footer ? config.footer.map(parseLayoutItem) : null;
    return {
      Component: withHelp(({ helpNode, ...props }) => {
        const fchildren = children.filter(c => c.canDisplay(props));
        return (
          <div
            key={key}
            className={classnames(
              'grb-form-block',
              topGutter && 'grb-form-top-gutter',
              bottomGutter && 'grb-form-bottom-gutter',
              className,
            )}
          >
            <header>
              {Icon && <Icon />}
              <Typography variant={variant}>{title}</Typography>
              {helpNode}
            </header>
            {!!fchildren.length && (
              <div className="grb-form-titled-block-content">
                {renderLayoutComponents(fchildren, props, true)}
              </div>
            )}
            {footer && (
              <footer className="grb-form-block-footer">
                {renderLayoutComponents(footer, props, true)}
              </footer>
            )}
          </div>
        );
      }, help),
    };
  },
  children: ({ config, key }) => {
    const { className } = config;
    return {
      canDisplay: ({ children }) => !!children,
      render: (props) => {
        const pps = props;
        return (
          <div key={key} className={classnames('grb-form-children', className)}>
            {pps.children}
          </div>
        );
      },
    };
  },
};

//
// Parse form configuration
//
const parseConfiguration = (configuration, options = {}, viewService) => {
  const editors = {};
  const validators = {};
  const editorNames = [];
  const editorFactories = options.editors
    ? { ...EditorFactories, ...options.editors } : EditorFactories;
  const layoutTypes = options.types ? { ...LayoutTypes, ...options.types } : LayoutTypes;
  //
  // Parse properties
  //
  if (configuration.properties) {
    configuration.properties.forEach((prop) => {
      if (!editorFactories[prop.type]) {
        console.error('No editor found for type ', prop.type, ' [property = ', prop.name, ']');
      } else {
        const propOptions = options.properties && options.properties[prop.name];
        const editor = new editorFactories[prop.type](prop, propOptions);
        editors[editor.name] = editor;
        editorNames.push(editor.name);
        validators[editor.name] = (prop.validators || []).map(parseValidator)
          .concat(options && options[editor.name] && options[editor.name].validators
              ? options[editor.name].validators.map(parseValidator) : []);
      }
    });
  }

  // Process dependencies
  Object.keys(editors).forEach((e) => {
    if (e.dependsOn) {
      e.dependsOn.forEach((d) => {
        if (!d.depedencies) {
          d.depedencies = [e];
        } else {
          d.depedencies.push(e);
        }
      });
    }
  });

  //
  // Parse layout
  //
  let ctnrCount = 1;
  const makeCtrnKey = () => {
    ctnrCount += 1;
    return `FormCtnr${ctnrCount}`;
  };
  let hasChildrenAnchor;

  const parseLayoutItem = (item) => {
    if (typeof item === 'string') {
      const Editor = editors[item];
      if (!Editor) {
        console.error('No editor found for property ', item);
        return null;
      }
      return {
        canDisplay: ({ filteredEditors }) => {
          return filteredEditors.indexOf(item) >= 0;
        },
        checkboxRow: Editor.property.type === 'bool',
        render: (props) => {
          const pps = props;
          const { errors, ...rest } = pps;
          return (
            <Editor.Component
              key={Editor.id || Editor.name}
              {...rest}
              editor={Editor}
              value={Editor.getValue(rest.data, rest)}
              errors={errors && errors[Editor.name] || undefined}
            />
          );
        },
      };
    }
    const Type = item.type && layoutTypes[item.type];
    if (!Type) {
      console.error('Unkown layout type ', item.type, ' in config:');
      console.error(item);
      return null;
    }
    if (item.type === 'children') {
      hasChildrenAnchor = true;
    }
    const children = item.children ? item.children.map(parseLayoutItem).filter(i => i) : [];
    const layoutItem = Type({
      editors,
      children,
      config: item,
      key: makeCtrnKey(),
      viewService,
      className: Type.childClassname,
      parseLayoutItem,
    });
    if (!layoutItem.canDisplay) {
      layoutItem.canDisplay = children
        ? (props) => Boolean(children.find(c => c.canDisplay(props)))
        : () => true;
      // layoutItem.canDisplay = () => true;
    }
    return layoutItem;
  };

  const getFormData = options.getFormData
    ? ((data) => options.getFormData(data, configuration))
    : (data => data);
  const setFormData = options.setFormData
    ? (data, formData) => options.setFormData(data, formData, configuration)
    : ((data, formData) => formData);

  const validate = (data, props, prevErrors = null) => {
    const errors = {};
    const formData = getFormData(data);
    let hasErrors = false;
    let errorsChanged; // Track if new processed errors are similar to prevErrors (optim)
    editorNames.forEach((name) => {
      const vs = validators[name];
      let editorErrors = null;
      if (vs) {
        const editor = editors[name];
        const value = editor.getValue(formData);
        editorErrors = vs.map(v => v(value, editor, formData, props)).filter(e => e);
        if (!editorErrors.length) {
          errors[name] = null;
          errorsChanged = errorsChanged || (prevErrors && !!prevErrors[name]);
        } else {
          hasErrors = true;
          if (prevErrors && prevErrors[name] && sameArrays(prevErrors[name], editorErrors)) {
            // Keep reference to previous errors if equal (optim)
            errors[name] = prevErrors[name];
          } else {
            errorsChanged = true;
            errors[name] = editorErrors;
          }
        }
      }
    });
    return errorsChanged ? (hasErrors && errors || null) : prevErrors;
  };

  return {
    editors,
    layout: configuration.layout ? configuration.layout.map(parseLayoutItem).filter(i => i) : [],
    variant: configuration.variant,
    validate,
    editorNames,
    getFormData,
    setFormData,
    hasChildrenAnchor,
    renderLayoutComponents,
    __parsedConfiguration: true,
  };
};

export { parseConfiguration, sameData };
